import React from "react";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { ReactComponent as Dropdown } from "../../../../assets/images/dropdown-arrow.svg";
import { AGENT_STATUS_OPTIONS } from "../../../../constants/peopleData";
import { peopleSelectMenuProps } from "../peopleSelectStyles";
import AgentSuspendedPill from "./AgentSuspendedPill";

const RADIUS = "8px";

const WIDTH_BY_VALUE = {
  accepting: 136,
  notAccepting: 168,
  offline: 111,
  invitationSent: 140,
  suspended: 136,
};

const DEFAULT_SELECT_WIDTH = 168;

const DOT_BY_VALUE = Object.fromEntries(
  AGENT_STATUS_OPTIONS.map((o) => [o.value, o.dot])
);

const LABEL_BY_VALUE = Object.fromEntries(
  AGENT_STATUS_OPTIONS.map((o) => [o.value, o.label])
);

function PresenceReadOnly({ value }) {
  const selectWidth = WIDTH_BY_VALUE[value] ?? DEFAULT_SELECT_WIDTH;

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        width: selectWidth,
        minWidth: selectWidth,
        maxWidth: selectWidth,
        height: 32,
        px: "10px",
        borderRadius: RADIUS,
        border: "1px solid #D5DCE1",
        backgroundColor: "#FFF",
        boxSizing: "border-box",
      }}
    >
      <Box
        component="span"
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: DOT_BY_VALUE[value] || "#93A1B0",
          flexShrink: 0,
        }}
      />
      <Box
        component="span"
        sx={{
          fontSize: 14,
          color: "#405261",
          lineHeight: 1.25,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          minWidth: 0,
          flex: 1,
        }}
      >
        {LABEL_BY_VALUE[value] || value}
      </Box>
    </Box>
  );
}

export default function AgentPresenceSelect({ value, onChange, onLogout, disabled }) {
  const normalizedValue =
    value === "accepting" ||
    value === "notAccepting" ||
    value === "offline" ||
    value === "invitationSent" ||
    value === "suspended"
      ? value
      : "offline";

  if (normalizedValue === "suspended") {
    return <AgentSuspendedPill />;
  }

  if (normalizedValue === "offline" || normalizedValue === "invitationSent") {
    return <PresenceReadOnly value={normalizedValue} />;
  }

  const selectWidth = WIDTH_BY_VALUE[normalizedValue] ?? DEFAULT_SELECT_WIDTH;
  const options = AGENT_STATUS_OPTIONS.filter(
    (o) => o.value !== "suspended" && o.value !== "offline" && o.value !== "invitationSent"
  );

  return (
    <FormControl
      size="small"
      sx={{
        width: selectWidth,
        minWidth: selectWidth,
        maxWidth: selectWidth,
        flexShrink: 0,
        m: 0,
        height: 32,
        "& .MuiInputBase-root": { height: 32, width: selectWidth },
      }}
    >
      <Select
        notched={false}
        variant="outlined"
        value={normalizedValue || ""}
        disabled={disabled || normalizedValue === "suspended"}
        onChange={(e) => {
          e.stopPropagation();
          const next = e.target.value;
          if (next !== "accepting" && next !== "notAccepting") return;
          onChange?.(next);
        }}
        IconComponent={Dropdown}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        renderValue={(selected) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              minWidth: 0,
              width: "100%",
              pr: 0.5,
            }}
          >
            <Box
              component="span"
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: DOT_BY_VALUE[selected] || "#93A1B0",
                flexShrink: 0,
              }}
            />
            <Box
              component="span"
              sx={{
                fontSize: 14,
                color: "#405261",
                lineHeight: 1.25,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                minWidth: 0,
                flex: 1,
              }}
            >
              {LABEL_BY_VALUE[selected] || selected}
            </Box>
          </Box>
        )}
        sx={{
          width: selectWidth,
          height: 32,
          minHeight: 32,
          maxHeight: 32,
          boxSizing: "border-box",
          borderRadius: RADIUS,
          backgroundColor: "#FFF",
          "& .MuiOutlinedInput-notchedOutline": { borderRadius: RADIUS, borderColor: "#D5DCE1" },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#C5CED6" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderRadius: RADIUS,
            borderColor: "#D5DCE1",
            borderWidth: "1px",
          },
          "& .MuiSelect-select": {
            py: 0,
            pl: "10px",
            pr: "34px !important",
            display: "flex",
            alignItems: "center",
            minHeight: 0,
            height: "100%",
            lineHeight: 1.25,
            boxSizing: "border-box",
          },
          "& .MuiSelect-icon": {
            right: 10,
            top: "50%",
            transform: "translateY(-50%)",
            width: 16,
            height: 16,
            pointerEvents: "none",
          },
        }}
        MenuProps={{
          disableScrollLock: true,
          ...peopleSelectMenuProps,
          PaperProps: {
            ...peopleSelectMenuProps.PaperProps,
            sx: {
              ...peopleSelectMenuProps.PaperProps.sx,
              borderRadius: RADIUS,
              width: Math.max(184, selectWidth),
              mt: "6px",
              zIndex: (theme) => theme.zIndex.modal + 1,
            },
          },
        }}
      >
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value} disableRipple>
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: opt.dot,
                  display: "inline-block",
                  flexShrink: 0,
                }}
              />
              {opt.label}
            </Box>
          </MenuItem>
        ))}
        {onLogout && normalizedValue !== "suspended" ? (
          <Box
            component="li"
            role="button"
            tabIndex={0}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onLogout();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onLogout();
              }
            }}
            sx={{
              listStyle: "none",
              borderTop: "1px solid #EEF2F6",
              mt: 0.5,
              px: 2,
              py: 1,
              cursor: "pointer",
              fontFamily: "Inter",
              fontSize: 14,
              color: "#405261",
              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: DOT_BY_VALUE.offline,
                  display: "inline-block",
                  flexShrink: 0,
                }}
              />
              Logout
            </Box>
          </Box>
        ) : null}
      </Select>
    </FormControl>
  );
}
